/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 * @description TourFormStep1
 */

import type { ButtonProps } from '@zcode-ui/controls'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import type { Origin } from '@zcode/nr-utils'
import type { Listable, Property } from '@zcode/state-type'
import type { FormikErrors, FormikState, FormikTouched } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { nextStep } from '../../../../modules/modals/actions/messageModal'
import { receiveSelectedDatetime } from '../../../../modules/multifamilyTourTimePicker/actions'
import { useDispatch } from '../../../../modules/useDispatch'
import { TourDatePicker } from '../../../TourBooking/TourDatePicker'
import { copyHoursMinutes, toDateStringInTz } from '../TourAvailabilityPicker/helpers'
import type { Values } from '../helpers'
import { TourTypeAndTimePicker } from './TourTypeAndTimePicker'

export interface TourFormStep1Props {
  entity: Listable | Property
  errors: FormikErrors<Values>
  hideTourTime?: boolean
  isSubmitting: boolean
  onTrackConversion: (
    event:
      | React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void
  origin: Origin
  resetForm: (nextState?: Partial<FormikState<Values>>) => void
  setFieldValue: <
    KeyValuePair extends NonNullable<{ [Key in keyof Values]: [Key, Values[Key]] }[keyof Values]>,
  >(
    key: KeyValuePair[0],
    value: KeyValuePair[1],
    shouldValidate?: boolean | undefined
  ) => void
  submitButton: React.ReactElement<ButtonProps<'button'>>
  submitCount: number
  touched: FormikTouched<Values>
  values: Values
}

export const TourFormStep1 = ({
  entity,
  errors,
  isSubmitting,
  onTrackConversion,
  origin,
  resetForm,
  setFieldValue,
  submitButton,
  submitCount,
  touched,
  values,
}: TourFormStep1Props) => {
  const dispatch = useDispatch()
  const isMobile = useSelector(selectIsMobile)
  const selectedTime = values.selectedDateTime?.selectedTime
  const onClickDate = useCallback(
    (date: Date) => {
      date = selectedTime ? copyHoursMinutes(selectedTime, date) : date
      dispatch(receiveSelectedDatetime(date))
      setFieldValue('selectedDateTime', {
        selectedDate: toDateStringInTz(date, entity.tz),
        selectedTime: date,
      })
    },
    [dispatch, entity.tz, selectedTime, setFieldValue]
  )
  const [lastSubmitCount, setLastSubmitCount] = useState(submitCount)
  const shouldGoToNextStep = useRef(false)
  useEffect(() => {
    // Formik's submit process is:
    // 1. Increment submitCount and set isSubmitting to true
    // 2. Run validators (and put errors into `errors`)
    // 3. If form is valid, call our onSubmit function
    // 4. Set isSubmitting to false
    // Since we want to react to "the user clicked submit and there are no errors",
    // we need to make this code run sometime after step 2, so we wait for step 4
    if (submitCount > lastSubmitCount && !isSubmitting) {
      if (!errors.selectedDateTime) {
        resetForm({ values })
        // resetForm dispatches some internal actions within formik to update its
        // state. We need to wait for those to be processed and react to re-render
        // so that our components will see that new, reset state. To do that, we
        // set this shouldGoToNextStep flag, then wait for submitCount to change
        // back to 0
        shouldGoToNextStep.current = true
      }
      setLastSubmitCount(submitCount)
    }
  }, [errors.selectedDateTime, isSubmitting, lastSubmitCount, resetForm, submitCount, values])
  useEffect(() => {
    if (submitCount === 0 && shouldGoToNextStep.current) {
      shouldGoToNextStep.current = false
      dispatch(nextStep())
    }
  }, [dispatch, shouldGoToNextStep, submitCount])
  const dateError = useMemo(() => {
    const error = errors?.selectedDateTime
    const touch = touched?.selectedDateTime
    // TODO: might need to adjust this condition!!!!!
    return typeof error !== 'string' &&
      // @ts-expect-error touch "can't" be an array (but it actually can)
      ((typeof touch !== 'boolean' && touch?.[0]?.selectedDate) || submitCount) &&
      // @ts-expect-error error "can't" be an object (but it actually can)
      typeof error?.selectedDate === 'string'
      ? // @ts-expect-error error "can't" be an object (but it actually can)
        error.selectedDate
      : undefined
  }, [errors.selectedDateTime, submitCount, touched.selectedDateTime])
  return (
    <>
      <TourDatePicker
        alignItems="flex-start"
        entity={entity}
        error={dateError}
        height={{ base: '112px', lg: '96px' }}
        mb={4}
        numberOfTilesOld={4}
        onChange={onClickDate}
        role="radio"
        showArrows={!isMobile}
        value={selectedTime}
      />
      <TourTypeAndTimePicker
        entity={entity}
        errors={errors}
        onTrackConversion={onTrackConversion}
        origin={origin}
        selectedDateTime={values.selectedDateTime}
        setFieldValue={setFieldValue}
        submitButton={submitButton}
        submitCount={submitCount}
        touched={touched}
      />
    </>
  )
}
